import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Mic, Square, Upload, Save, Loader2, FileText } from "lucide-react";
import { TranscriptEditor } from "../TranscriptEditor";
import { API } from "../../App";

import { LANGUAGES as LANGS } from "../../lib/languages";
const selectCls = "px-2 py-1.5 text-sm border rounded-lg bg-background border-border";

export const SessionTranscriptTab = ({ report, onUpdated }) => {
  const [transcript, setTranscript] = useState(report.transcript || "");
  const [language, setLanguage] = useState(report.language || "fr");
  const [speakers, setSpeakers] = useState(report.speakers || 2);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState("");
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileRef = useRef(null);
  const dirty = transcript.trim() !== (report.transcript || "").trim() || language !== (report.language || "fr") || speakers !== (report.speakers || 2);

  useEffect(() => { setTranscript(report.transcript || ""); }, [report.report_id, report.transcript]);

  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices?.().then((list) => {
      const mics = list.filter((d) => d.kind === "audioinput");
      setDevices(mics);
      if (mics[0] && !deviceId) setDeviceId(mics[0].deviceId);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (extra = {}) => {
    setBusy("save");
    try {
      const res = await axios.put(`${API}/session-reports/${report.report_id}`, { transcript, language, speakers, ...extra }, { withCredentials: true });
      onUpdated(res.data);
      toast.success("Transcription enregistrée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const transcribeBlob = async (blob, name) => {
    setBusy("transcribe");
    try {
      const fd = new FormData();
      fd.append("audio", blob, name);
      fd.append("language", language);
      fd.append("speakers", String(speakers));
      const res = await axios.post(`${API}/session-reports/transcribe-audio`, fd, { withCredentials: true });
      setTranscript((t) => (t.trim() ? `${t.trim()}\n\n${res.data.transcription}` : res.data.transcription));
      toast.success("Transcription terminée — pensez à enregistrer");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de transcription"); }
    finally { setBusy(""); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: deviceId ? { deviceId: { exact: deviceId } } : true });
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices(list.filter((d) => d.kind === "audioinput"));
      const rec = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm" });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 1000) transcribeBlob(blob, "seance.webm");
      };
      rec.start(1000);
      recRef.current = rec;
      setRecording(true); setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch { toast.error("Micro inaccessible : vérifiez les autorisations du navigateur"); }
  };

  const stopRecording = () => { recRef.current?.stop(); setRecording(false); };

  const importFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    const fd = new FormData();
    if (["txt", "pdf"].includes(ext)) {
      setBusy("import");
      try {
        fd.append("document", file, file.name);
        const res = await axios.post(`${API}/session-reports/extract-text`, fd, { withCredentials: true });
        setTranscript((t) => (t.trim() ? `${t.trim()}\n\n${res.data.text}` : res.data.text));
        toast.success(`${file.name} importé — pensez à enregistrer`);
      } catch (e) { toast.error(e.response?.data?.detail || "Import impossible"); }
      finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
    } else {
      await transcribeBlob(file, file.name);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;

  return (
    <div className="space-y-4" data-testid="session-transcript-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Langue</Label><br />
          <select className={selectCls} value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="transcript-language-select">
            {LANGS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select></div>
        <div><Label className="text-xs">Intervenants</Label><br />
          <select className={selectCls} value={speakers} onChange={(e) => setSpeakers(Number(e.target.value))} data-testid="transcript-speakers-select">
            {[2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
          </select></div>
        <div className="min-w-[200px] flex-1"><Label className="text-xs">Microphone</Label><br />
          <select className={`${selectCls} w-full`} value={deviceId} onChange={(e) => setDeviceId(e.target.value)} disabled={recording} data-testid="transcript-mic-select">
            {devices.length === 0 && <option value="">Micro par défaut</option>}
            {devices.map((d, i) => <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Micro ${i + 1}`}</option>)}
          </select></div>
        {!recording ? (
          <Button onClick={startRecording} disabled={busy !== ""} className="gap-2" data-testid="transcript-record-btn"><Mic className="w-4 h-4" /> Enregistrer en direct</Button>
        ) : (
          <Button onClick={stopRecording} variant="destructive" className="gap-2 animate-pulse" data-testid="transcript-stop-btn"><Square className="w-4 h-4" /> Arrêter · {mmss}</Button>
        )}
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy !== "" || recording} className="gap-2" data-testid="transcript-import-btn"><Upload className="w-4 h-4" /> Importer (.txt, .pdf, audio)</Button>
        <input ref={fileRef} type="file" accept=".txt,.pdf,.mp3,.m4a,.wav,.webm,.ogg,.mp4" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} data-testid="transcript-file-input" />
      </div>
      {busy === "transcribe" && <p className="text-sm text-primary flex items-center gap-2" data-testid="transcript-busy"><Loader2 className="w-4 h-4 animate-spin" /> Transcription en cours (Gemini)… cela peut prendre 1 à 3 minutes.</p>}
      {busy === "import" && <p className="text-sm text-primary flex items-center gap-2"><FileText className="w-4 h-4 animate-pulse" /> Lecture du document…</p>}
      <TranscriptEditor value={transcript} onChange={setTranscript} disabled={busy !== "" || recording} rows={14} testId="session-transcript" />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">{transcript.trim() ? `${transcript.trim().length.toLocaleString("fr-FR")} caractères${dirty ? " · modifications non enregistrées" : ""}` : "Aucune transcription — enregistrez, collez ou importez."}</span>
        <Button onClick={() => save()} disabled={busy !== "" || !dirty} className="gap-2" data-testid="transcript-save-btn"><Save className="w-4 h-4" /> {busy === "save" ? "Enregistrement..." : "Enregistrer la transcription"}</Button>
      </div>
    </div>
  );
};
